import {MenuList, MenuItem, Box, Typography, Link} from '@mui/material';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import {getOldLink} from '@jobseeker/lotus/utils';
import {getIconClassType} from '@jobseeker/lotus/helpers/notificationIcon';

import {Image} from '@jobseeker/lotus/components/Image';

import type {DropItemType} from '@jobseeker/lotus/components/Drop/types';
import type {Theme} from '@mui/material/styles';
import {openUrl, deferTask} from '@jobseeker/common-core/utils/common';
import {TrackEventUBA, getKeyFromPath} from '@jobseeker/common-core/utils';
import {initiateRecruiterChat} from '@jobseeker/common-core/utils/recruiterChat';
import {useEffect} from 'react';

type Props = {
  notificationList: DropItemType[];
  forwardLink?: string;
  theme: Theme;
  onMenuClose: () => void;
  markNotificationRead: (
    notificationId: number,
    notificationStatus: number,
    type: number
  ) => void;
};

const NotificationList = (props: Props) => {
  const {
    notificationList,
    forwardLink,
    theme,
    onMenuClose,
    markNotificationRead,
  } = props;
  const initiateChat = (userId: number, recruiterId: number) => {
    initiateRecruiterChat({
      type: 'j', // r- recruiter, j- jobseeker
      userId,
      recipientId: recruiterId,
      tokenName: `${process.env.NEXT_PUBLIC_APP_NAME?.toUpperCase()}_CK1`,
    });
  };

  useEffect(() => {
    if (notificationList?.length > 0) {
      TrackEventUBA('NotificationView', 'view', {
        ref: getKeyFromPath('ref') || '',
      });
    }
  }, []);

  const handleNotificationItemClick = (
    id: number,
    status: number,
    redirectUrl: string,
    type: number,
    notificationJson: string,
    userId: number
  ) => {
    TrackEventUBA('NotificationClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      subtype: type === 2 ? 'status change notification' : '',
    });

    deferTask(() => {
      onMenuClose();
      markNotificationRead(id, status, type);
      if (redirectUrl) {
        openUrl(getOldLink(redirectUrl), '_blank');
      }

      if (type == 11) {
        const {recId: recruiterId} =
          typeof notificationJson === 'string'
            ? JSON.parse(notificationJson)
            : notificationJson;
        initiateChat(userId, recruiterId);
      }
    });
  };

  const NotificationIcon = ({type}: {type: number}) => {
    type NotificationType = {icon: string; background: string};

    if (!getIconClassType(type).icon) {
      return '';
    }

    return (
      <Image
        src={getIconClassType(type).icon}
        alt={'notification_icon'}
        width={50}
        height={50}
        style={{
          minWidth: '50px',
          minHeight: '50px',
          borderRadius: '10px',
          padding: '10px 14px',
          float: 'left',
          marginRight: '11px',
          background: getIconClassType(type).color,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          border: '0.6px solid #7E7E7E33',
        }}
      />
    );
  };

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  return (
    <>
      {notificationList?.length > 0 && (
        <MenuList
          sx={{
            pt: {xxs: 0, md: isAppIimjobs ? theme.spacing(2) : 0},
          }}
        >
          {notificationList?.map((item, index) => {
            const {
              id,
              message,
              created_date,
              createdOn,
              redirectUrl,
              redirect,
              notification_json,
              type,
              user_id,
              status,
            } = item || {};

            // intentionally marked == to disable type checking as currently php system do save the numeric as string in redis
            const isRead = status == 2;

            const createdDate = created_date || createdOn;

            const redirectTo = redirect || redirectUrl;
            const notificationSpan = createdDate
              ? dateToDuration(createdDate)?.replace('-', '')
              : '';

            return (
              <MenuItem
                key={`lotus-headernav-${index}`}
                onClick={event => {
                  handleNotificationItemClick(
                    id,
                    status,
                    redirectTo,
                    type,
                    notification_json,
                    user_id
                  );
                }}
                sx={{
                  backgroundColor: isRead
                    ? '#fff'
                    : isAppIimjobs
                    ? '#91B87929'
                    : '#f5f8fc',
                  ':hover': {
                    zIndex: 2,
                    backgroundColor: isRead
                      ? '#fff'
                      : isAppIimjobs
                      ? '#91B87929'
                      : '#f5f8fc',
                    boxShadow: isAppIimjobs
                      ? '0px 12px 17px 0px rgba(19, 21, 23, 0.06)'
                      : '',
                  },
                  ':focus': {
                    backgroundColor: isRead
                      ? '#fff'
                      : isAppIimjobs
                      ? '#91B87929'
                      : '#f5f8fc',
                  },
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  padding: 0,
                  display: 'block',
                  borderBottom: isAppIimjobs
                    ? !(notificationList.length === index + 1)
                      ? '1px solid rgba(126, 126, 126, 0.2)'
                      : ''
                    : '1px solid #eff3fa',
                }}
              >
                <Box
                  display={'block'}
                  sx={
                    isAppIimjobs
                      ? {
                          py: theme.spacing(2),
                          px: theme.spacing(3),
                        }
                      : {
                          pt: theme.spacing(2),
                          pb: theme.spacing(4.125),
                          pl: theme.spacing(1.5),
                          pr: theme.spacing(3),
                        }
                  }
                >
                  <Box display="flex">
                    {!isAppIimjobs && <NotificationIcon type={type} />}
                    <Typography
                      component="div"
                      fontSize={theme.typography.pxToRem(
                        isAppIimjobs ? 12 : 14
                      )}
                      fontWeight={theme.typography.fontWeightMedium}
                      color={isAppIimjobs ? 'text.secondary' : 'rgba(0,0,0,.8)'}
                      dangerouslySetInnerHTML={{__html: message}}
                    />
                  </Box>

                  {notificationSpan && (
                    <Typography
                      fontSize={theme.typography.pxToRem(
                        isAppIimjobs ? 10 : 14
                      )}
                      fontWeight={theme.typography.fontWeightRegular}
                      color={isAppIimjobs ? '#B2ADAD' : 'rgba(0,0,0,.3)'}
                      pt={theme.spacing(0.5)}
                      sx={
                        isAppIimjobs
                          ? {}
                          : {
                              float: 'right',
                            }
                      }
                    >
                      {notificationSpan}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </MenuList>
      )}

      {!notificationList.length && (
        <Typography
          pt={theme.spacing(2)}
          sx={{
            mr: theme.spacing(1.5),
            ml: theme.spacing(3),
            mb: theme.spacing(2),
          }}
        >{`You don't have any new notification`}</Typography>
      )}

      {forwardLink && (notificationList.length >= 4 || !isAppIimjobs) && (
        <Link
          sx={
            isAppIimjobs
              ? {
                  display: 'block',
                  textAlign: 'center',
                  fontSize: theme.typography.pxToRem(12),
                  fontweight: theme.typography.fontWeightMedium,
                  color: 'primary.main',
                  pb: theme.spacing(2),
                  opacity: 1,
                  textDecoration: 'none',
                }
              : {
                  width: '100%',
                  height: 70,
                  border: '10px solid #fff',
                  borderRadius: '3px',
                  backgroundColor: '#eff3fa',
                  fontSize: 16,
                  display: 'inline-block',
                  paddingTop: '14px',
                  textAlign: 'center',
                  color: 'rgba(0, 0, 0, .8)',
                  textDecoration: 'none',
                }
          }
          href={forwardLink}
          target={'_blank'}
          onClick={e => {
            e.preventDefault();
            if (forwardLink) {
              deferTask(() => {
                openUrl(forwardLink, '_blank');
              });
            }
          }}
        >
          {isAppIimjobs ? 'See More' : 'Show all Notifications'}
        </Link>
      )}
    </>
  );
};

export default NotificationList;
